import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ShieldCheck, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const TwoFactorAuthPage = () => {
  const [config, setConfig] = useState({ enabled: false, delivery_method: 'email', code_length: 6, code_expiry_minutes: 5, exempt_roles: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const roles = ['admin', 'manager', 'loan_officer', 'accountant', 'teller', 'auditor'];

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/two-factor-config'); setConfig(res.data); }
    catch {}
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try { await api.put('/two-factor-config', config); toast.success('Two-factor configuration saved'); }
    catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const toggleExempt = (role) => {
    const exempt = config.exempt_roles || [];
    setConfig({ ...config, exempt_roles: exempt.includes(role) ? exempt.filter(r => r !== role) : [...exempt, role] });
  };

  if (loading) return <AdminSubPage title="Two-Factor Authentication" breadcrumb={['Admin', 'System', 'Two-Factor Auth']} backPath="/dashboard/admin/system"><div className="text-center py-8 text-gray-400">Loading...</div></AdminSubPage>;

  return (
    <AdminSubPage title="Two-Factor Authentication" breadcrumb={['Admin', 'System', 'Two-Factor Auth']} backPath="/dashboard/admin/system">
      <div className="max-w-2xl space-y-6">
        {/* Main Toggle */}
        <div className="bg-white border border-gray-200 p-6" data-testid="2fa-config">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className={`w-8 h-8 ${config.enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
              <div>
                <h3 className="font-semibold text-[#1f1f1f]">Two-Factor Authentication</h3>
                <p className="text-sm text-gray-500">Require verification code on login</p>
              </div>
            </div>
            <Switch checked={config.enabled} onCheckedChange={v => setConfig({...config, enabled: v})} data-testid="2fa-toggle" />
          </div>

          {config.enabled && (
            <div className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Delivery Method</Label>
                  <Select value={config.delivery_method} onValueChange={v => setConfig({...config, delivery_method: v})}>
                    <SelectTrigger data-testid="2fa-delivery-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="totp">Authenticator App (TOTP) — recommended</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                    </SelectContent>
                  </Select>
                  {config.delivery_method === 'totp' && (
                    <p className="text-[11px] text-emerald-700 mt-1">Users will be prompted to scan a QR code from Google Authenticator / Authy / 1Password / Microsoft Authenticator on their next login.</p>
                  )}
                </div>
                <div><Label>Code Length</Label>
                  <Select value={String(config.code_length)} onValueChange={v => setConfig({...config, code_length: parseInt(v)})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 digits</SelectItem>
                      <SelectItem value="6">6 digits</SelectItem>
                      <SelectItem value="8">8 digits</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Code Expiry (minutes)</Label><Input type="number" value={config.code_expiry_minutes} onChange={e => setConfig({...config, code_expiry_minutes: parseInt(e.target.value) || 5})} className="w-32" /></div>

              <div>
                <Label className="mb-2 block">Exempt Roles (bypass 2FA)</Label>
                <div className="flex flex-wrap gap-2">
                  {roles.map(role => (
                    <button key={role} onClick={() => toggleExempt(role)} className={`px-3 py-1.5 text-xs border transition-colors capitalize ${(config.exempt_roles || []).includes(role) ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-white text-gray-600 border-gray-300'}`} data-testid={`exempt-role-${role}`}>
                      {role.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-[#1f1f1f] text-white" data-testid="2fa-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>
    </AdminSubPage>
  );
};
export default TwoFactorAuthPage;
